import {
  CurrencyCode,
  sendAnalyticsEvent,
  UnknownEvent,
  useSession,
} from '@faststore/sdk'
import { useCallback } from 'react'
import { ITEMS_PER_PAGE } from 'src/constants'
import { useListNameContext } from 'src/contexts/list-name-context'
import {
  useAuthenticatedUserToken,
  useUserToken,
} from 'src/sdk/analytics/hooks/useUserToken'
import {
  AutocompleteSuggestionClickedEvent,
  AutocompleteSuggestionsShownEvent,
} from 'src/sdk/analytics/types'
import capitalizeFirstLetter from 'src/utils/stringUtils'
import { useSearchParams } from '../sections/AddedToCart/utils/utils'
import type { AutocompleteAnalyticsData, Filter, VariantHit } from './algolia'
import { mainIndex } from './algoliaSearchConfig'
import { preAppliedFiltersKey } from './routing'

export const SHOW_PROMO_IN_NO_RESULTS = false

// Empty state: top-level pet categories
// TO-DO Wave successive? CMS/env
export const EMPTY_STATE_PET_CATEGORIES = [
  { label: 'Cane', href: '/cane' },
  { label: 'Gatto', href: '/gatto' },
  { label: 'Roditori', href: '/piccoli-animali/roditori' },
  { label: 'Uccelli', href: '/piccoli-animali/uccelli' },
  { label: 'Pesci', href: '/piccoli-animali/pesci' },
  { label: 'Tartarughe', href: '/piccoli-animali/tartarughe' },
  { label: 'Rettili', href: '/piccoli-animali/rettili' },
  { label: 'Persona e casa', href: '/persona-e-casa' },
] as const

const filtersMap = {
  brand: 'marca',
  selling_price: 'prezzo',
  private_label: 'prodotti_esclusivi',
  'category-1': 'reparto',
  'category-2': 'sottoreparto',
  'category-3': 'categoria',
  'category-4': 'sottocategoria',
  'category-5': 'tipo_di_prodotto',
  forma: 'forma',
  'formato-del-prodotto': 'formato_del_prodotto',
  funzione: 'funzione',
  peso_reale: 'peso_della_confezione_(kg/l)',
  'product-volume': 'volume',
  productclusterids: 'productclusterids',
  productclusternames: 'productclusternames',
  texture: 'texture',
  'tipo-di-alimento': 'tipo_di_alimento',
  'tipo-di-cibo': 'tipo_di_cibo',
  'tipo-di-pesce': 'tipo_di_pesce',
  'tipo-di-roditore': 'tipo_di_roditore',
  'tipo-di-uccello': 'tipo_di_uccello',
  eta: 'eta',
  colore: 'colore',
  formato: 'formato',
  razza: 'razza',
  caratteristica: 'caratteristica',
  conservazione: 'conservazione',
  'formato-del-pack': 'formato_del_pack',
  gusto: 'gusto',
  'funzione-alimentare': 'funzione_alimentare',
  materiale: 'materiale',
  taglia: 'taglia',
  'caratteristica-nutrizionale': 'caratteristica_nutrizionale',
  consistenza: 'consistenza',
  stores_in_stock: 'disponibilita_in_negozio',
  // list_price: 'prezzo',
} as Record<string, string>

export const facetsMap = {
  'biokat-s': "BIOKAT'S",
  'cat-s-best': "CAT'S BEST",
  'erba-piu-': "ERBA PIU'",
  'gill-s': "GILL'S",
  'hill-s': "HILL'S",
  'hill-s-prescription-diet-cane': "HILL'S PRESCRIPTION DIET CANE",
  'hill-s-science-plan-cane': "HILL'S SCIENCE PLAN CANE",
  'hill-s-science-plan-gatto': "HILL'S SCIENCE PLAN GATTO",
  'kitty-s-cuisine': "KITTY'S CUISINE",
  'mobilita-': "MOBILITA'",
  'sensibilita-': "SENSIBILITA'",
  'agrifarma---default-brand': 'Agrifarma - Default Brand',
  'm-pets': 'M-PETS',
  'test-idntt': 'test-idntt',
  'yes-': 'YES!',
  // "yes-": "Yes!",
  'loveyou--10--extra-': 'LOVEYOU (10% extra)',
  'carni---derivati': 'CARNI & DERIVATI',
  'edgard---cooper': 'EDGARD & COOPER',
  'luna-teo': 'Luna&Teo',
  'n-d': 'N&D',
  'allevatori--b2b-': 'Allevatori (B2B)',
  'cliniche-veterinarie--b2b-': 'Cliniche veterinarie (B2B)',
  'enpa--b2b-': 'ENPA (B2B)',
  'arcacard-imperdibili': 'arcacard_Imperdibili',
  'lengow-1': 'lengow_1',
  'lengow-2': 'lengow_2',
  cincilla: 'CINCILLÀ',
  'funzionalita-cardiaca': 'FUNZIONALITÀ CARDIACA',
  'funzionalita-del-fegato': 'FUNZIONALITÀ DEL FEGATO',
  'funzionalita-intestinale': 'FUNZIONALITÀ INTESTINALE',
  mobilita: 'MOBILITÀ',
  novita: 'Novità',
  pate: 'PATÈ',
  'sensibilita-alimentari': 'SENSIBILITÀ ALIMENTARI',
  'tutte-le-eta': 'TUTTE LE ETÀ',
} as Record<string, string>

export const scalarKeys = new Set(['page', 'sort', 'q', 'sortBy', 'query']) // These are the filter keys that must be skipped when parsing the url and when handling the pre-applied filters

export const formatCategories = (item: string) => {
  if (facetsMap[item]) {
    return facetsMap[item]
  }

  if (item.includes('-')) {
    // ex: /cibo-secco
    const splittedItem = item.split('-')
    const fixedSplittedItem = splittedItem.map((item) => {
      if (item === 'e') return item
      return capitalizeFirstLetter(item)
    })
    return fixedSplittedItem.join(' ')
  }
  return capitalizeFirstLetter(item)
}

export const getPreappliedFilters = (
  selectedFacets: Array<{ key: string; value: string }>
) => {
  if (!selectedFacets || selectedFacets.length === 0) return {}

  const preAppliedFilters = selectedFacets.reduce((acc, curr) => {
    const formattedValue = formatCategories(curr.value)
    // const fixedValue =
    //   curr.key === 'brand' ? formattedValue.toUpperCase() : formattedValue
    acc[curr.key] = [formattedValue]

    return acc
  }, {} as Record<string, string[]>)

  typeof window !== 'undefined' &&
    window.sessionStorage.setItem(
      preAppliedFiltersKey,
      JSON.stringify(preAppliedFilters)
    ) // we set the preAppliedFilters in sessionStorage to be able to use it in useItemListName
  return preAppliedFilters
}

// export const getPreappliedFilters = (pathname: string) => {
//   // Starting from url we get all categories levels to pre-apply the right filters in category PLPs
//   const splittedPathname = pathname.split('/').filter(Boolean)
//   const formattedCategories = splittedPathname.map(formatCategories)

//   const returnObj = formattedCategories.reduce((acc, curr, index) => {
//     if (curr.toLowerCase() === 'brand') return acc

//     const keyToFix = pathname.includes('/brand/')
//       ? 'brand'
//       : `category-${index + 1}`
//     const value = pathname.includes('/brand/') ? curr.toUpperCase() : curr
//     const fixedKey = mapFiltersToUrl(keyToFix)
//     acc[fixedKey] = [value]
//     return acc
//   }, {} as Record<string, string[]>)

//   return returnObj
// }

// export const getPreappliedFilters = (pathname: string) => {
//   // Starting from url we get all categories levels to pre-apply the right filters in category PLPs
//   const splittedPathname = pathname.split('/').filter(Boolean)
//   let evaluatedCategories: string[] = []
//   const fixedCategories = {} as Record<string, string[]>

//   const formattedCategories = splittedPathname.map(formatCategories)
//   formattedCategories.forEach((item, index) => {
//     evaluatedCategories.push(item)
//     const preAppliedCategories = evaluatedCategories.join(' > ')
//     const key = `product_categories.lvl${index}`
//     const value = [preAppliedCategories]
//     fixedCategories[key] = value
//   })

//   return fixedCategories
// }

// export const getPreappliedFilters = (pathname: string) => {
//   // Starting from url we get all categories levels to pre-apply the right filters in category PLPs
//   const splittedPathname = pathname.split('/').filter(Boolean)
//   const fixedCategories = splittedPathname.map(formatCategories)

//   if (!fixedCategories.length) return null

//   const level = fixedCategories.length - 1
//   const fixedKey = mapFiltersToUrl(`product_categories.lvl${level}`)
//   // We return just the last category level and match it with Algolia fields
//   return { [fixedKey]: [fixedCategories.join(' > ')] }
//   // return `product_categories.lvl${level}:"${fixedCategories.join(' > ')}"`
// }

export const getIsPreAppliedFilter = (
  preAppliedFilters: Filter | null,
  newFilter: Filter
) => {
  let isPreApplied = false
  if (!preAppliedFilters) return isPreApplied

  const newFilterEntries = Object.entries(newFilter)
  const [key, value] = newFilterEntries[0]
  const preAppliedEntries = Object.entries(preAppliedFilters)
  const preAppliedKey = preAppliedEntries.find((entry) => entry[0] === key)?.[0]

  // If the key is pre applied, we check if the value is in the pre applied filters
  if (preAppliedKey && Array.isArray(value)) {
    isPreApplied = value.every((val) =>
      preAppliedFilters[preAppliedKey]
        .map((preAppVal) => preAppVal.toLowerCase())
        .includes(val.toLowerCase())
    )
  }

  return isPreApplied
}

export const mapFiltersToUrl = (key: string) => {
  return filtersMap[key] ?? key
}

// export const mapUrlToFilters = (key: string) => {
//   const entries = Object.entries(filtersMap)
//   const fixedKey = entries.find((entry) => entry[1] === key)?.[0]
//   return fixedKey ?? key
// }

export const formatAttribute = (attribute: string) => {
  // This function will replace the underscores in the attribute name with spaces and other fixes
  const replacedAttribute = capitalizeFirstLetter(
    attribute
      .toLowerCase()
      .replaceAll('-', ' ')
      .replaceAll('_', ' ')
      .replaceAll("eta'", 'età')
      .replaceAll('eta', 'età')
      .replaceAll('eta-', 'età')
      .replaceAll('et-', 'età')
      .replaceAll("disponibilita'", 'disponibilità')
      .replaceAll('disponibilita', 'disponibilità')
      .replaceAll('disponibilita-', 'disponibilità')
      .replaceAll('disponibilit-', 'disponibilità')
      .replaceAll("piu'", 'più')
      .replaceAll('piu', 'più')
      .replaceAll('piu-', 'più')
      .replaceAll('pi-', 'più')
      .replaceAll("lovedi'", 'lovedì')
      .replaceAll('lovedi', 'lovedì')
      .replaceAll('lovedi-', 'lovedì')
      .replaceAll('loved-', 'lovedì')
      .replaceAll("sensibilita'", 'sensibilità')
      .replaceAll('sensibilita', 'sensibilità')
      .replaceAll('sensibilita-', 'sensibilità')
      .replaceAll('sensibilit-', 'sensibilità')
      .replaceAll("mobilita'", 'mobilità')
      .replaceAll('mobilita', 'mobilità')
      .replaceAll('mobilita-', 'mobilità')
      .replaceAll('mobilit-', 'mobilità')
      .replaceAll("funzionalita'", 'funzionalità')
      .replaceAll('funzionalita', 'funzionalità')
      .replaceAll('funzionalita-', 'funzionalità')
      .replaceAll('funzionalit-', 'funzionalità')
      .replaceAll("pate'", 'patè')
      .replaceAll('pate', 'patè')
      .replaceAll('pate-', 'patè')
      .replaceAll("pat-e'", 'patè')
      .replaceAll("cincilla'", 'cincillà')
      .replaceAll('cincilla', 'cincillà')
      .replaceAll('cincilla-', 'cincillà')
      .replaceAll('cincill-', 'cincillà')
  )

  // const rangePattern = /^\s*\d+(?:\.\d+)?\s*-\s*\d+(?:\.\d+)?\s*[a-zA-Z]*\s*$/
  // const mustNotReplaceDash =
  //   rangePattern.test(attribute) ||
  //   replacedAttribute.includes('€ -') ||
  //   replacedAttribute.includes('ml') ||
  //   replacedAttribute.includes('ML') ||
  //   replacedAttribute.includes('lt') ||
  //   replacedAttribute.includes('LT')
  // const fixedAttribute = mustNotReplaceDash
  //   ? replacedAttribute
  //   : replacedAttribute.replaceAll('-', ' ')
  // return fixedAttribute
  return replacedAttribute
}

export const getSortIndexes = (mustShowBestSeller: boolean = true) => {
  return [
    // Here we map the virtual indexes for the SortBy component
    {
      label: 'I più rilevanti',
      value: 'variants_it',
    },
    ...(mustShowBestSeller
      ? [
          {
            label: 'I più venduti',
            value: 'variants_it_bestseller_desc',
          },
        ]
      : []),
    {
      label: 'Novità',
      value: 'variants_it_newest_desc',
    },
    {
      label: 'Prezzo più basso',
      value: 'variants_it_price_asc',
    },
    {
      label: 'Prezzo più alto',
      value: 'variants_it_price_desc',
    },
    {
      label: 'Prezzo al Kg/Lt più basso',
      value: 'variants_it_eur_kg_asc',
    },
    {
      label: 'Prezzo al Kg/Lt più alto',
      value: 'variants_it_eur_kg_desc',
    },
    // {
    //   label: 'A-Z',
    //   value: 'variants_it_alpha_asc',
    // },
    // {
    //   label: 'Z-A',
    //   value: 'variants_it_alpha_desc',
    // },
  ]
}

export const useLinkProps = (variant: VariantHit, resultsCount: number) => {
  const {
    currency: { code },
  } = useSession()
  const userToken = useUserToken()
  const authenticatedUserToken = useAuthenticatedUserToken()
  const { q } = useSearchParams() || {}
  const { listName: itemListName } = useListNameContext()

  if (!variant) return

  let slug: string = variant.variant_url
  if (!slug.includes(variant.skuId)) {
    const splitted = slug.split('/')
    slug = `/${splitted[1]}-${variant.skuId}/p`
  }

  const handleOnClick = useCallback(() => {
    const itemCategory = variant.categoryPath.split('/')
    const pageSize = ITEMS_PER_PAGE
    const page = Math.ceil(variant.index / pageSize)
    const isKit = variant.is_kit

    sendAnalyticsEvent<UnknownEvent>({
      name: 'select_item',
      params: {
        // Adding optional kit parameters
        ...(isKit && {
          bundle_label: variant.productName,
          bundle_discount: +(
            variant.list_price - variant.selling_price
          ).toFixed(2),
          bundle_discount_percentage: +(
            1 -
            variant.selling_price / variant.list_price
          ).toFixed(2),
        }),
        algolia_user_token: userToken,
        ...(authenticatedUserToken && {
          algolia_authenticated_user_token: authenticatedUserToken,
        }),
        queryID: variant.__queryID,
        page,
        page_size: pageSize,
        item_list_name: itemListName,
        // item_list_name: JSON.parse(itemListName || '{}'),
        results_count: resultsCount,
        items: [
          {
            object_id: variant.objectID,
            index: variant.index,
            item_list_name: itemListName,
            // item_list_id: itemCategory.join(' '),
            item_id: variant.productId,
            item_name: variant.productName,
            item_brand: variant.brand,
            item_variant: variant.skuId,
            item_category: itemCategory,
            price: variant.selling_price,
            position: variant.index,
            discount: +(variant.list_price - variant.selling_price).toFixed(2),
            full_price: variant.list_price,
            discount_percentage: +(
              1 -
              variant.selling_price / variant.list_price
            ).toFixed(2),
            currency: code as CurrencyCode,
            item_variant_name: variant.skuName,
            product_reference_id: Number(variant.refId),
            quantity: 1,
            coupon: null,
            search_term: q,
            unitMultiplier: variant.unit_multiplier,
            // Adding optional kit parameters
            ...(isKit && {
              bundle_label: variant.productName,
              bundle_discount: +(
                variant.list_price - variant.selling_price
              ).toFixed(2),
              bundle_discount_percentage: +(
                1 -
                variant.selling_price / variant.list_price
              ).toFixed(2),
            }),
          },
        ],
      },
    })

    window.localStorage.setItem(
      'algolia-search-info',
      JSON.stringify({
        queryID: variant.__queryID,
        indexName: variant.__indexName,
        skuId: variant.skuId,
        itemListName,
      })
    )

    // handleEvent({
    //   name: 'search_select_item',
    //   params: {
    //     url: window.location.href,
    //     term: q ?? '',
    //     items: [
    //       {
    //         item_id: variant.productId,
    //         index: variant.index,
    //       },
    //     ],
    //   },
    // })
  }, [
    code,
    variant,
    q,
    itemListName,
    userToken,
    authenticatedUserToken,
    resultsCount,
  ])

  return {
    to: `${slug}`,
    handleOnClick,
    'data-testid': 'product-link',
  }
}

export const useSelectItemSearchBar = (variant: VariantHit) => {
  const {
    currency: { code },
  } = useSession()
  const userToken = useUserToken()
  const authenticatedUserToken = useAuthenticatedUserToken()

  if (!variant) return

  const handleOnClick = useCallback(
    (term: string, pageSize: number, resultsCount: number) => {
      const itemListName = `lt=autocomplete||topic=product||state=typing${
        term ? `||term=${term}` : ''
      }`
      // const itemListName = {
      //   lt: 'autocomplete',
      //   topic: 'product',
      //   state: 'typing',
      //   ...(term ? { term } : {}),
      // }
      const itemCategory = variant.categoryPath.split('/')
      const page = 1
      const isKit = variant.is_kit

      sendAnalyticsEvent<UnknownEvent>({
        name: 'select_item',
        params: {
          // Adding optional kit parameters
          ...(isKit && {
            bundle_label: variant.productName,
            bundle_discount: +(
              variant.list_price - variant.selling_price
            ).toFixed(2),
            bundle_discount_percentage: +(
              1 -
              variant.selling_price / variant.list_price
            ).toFixed(2),
          }),
          algolia_user_token: userToken,
          ...(authenticatedUserToken && {
            algolia_authenticated_user_token: authenticatedUserToken,
          }),
          queryID: variant.__queryID,
          page,
          page_size: pageSize,
          results_count: resultsCount,
          item_list_name: itemListName,
          items: [
            {
              object_id: variant.objectID,
              index: variant.__position,
              item_list_name: itemListName,
              // item_list_id: itemCategory.join(' '),
              item_id: variant.productId,
              item_name: variant.productName,
              item_brand: variant.brand,
              item_variant: variant.skuId,
              item_category: itemCategory,
              price: variant.selling_price,
              position: variant.__position,
              discount: +(variant.list_price - variant.selling_price).toFixed(
                2
              ),
              full_price: variant.list_price,
              discount_percentage: +(
                1 -
                variant.selling_price / variant.list_price
              ).toFixed(2),
              currency: code as CurrencyCode,
              item_variant_name: variant.skuName,
              product_reference_id: Number(variant.refId),
              quantity: 1,
              coupon: null,
              search_term: term,
              unitMultiplier: variant.unit_multiplier,
              // Adding optional kit parameters
              ...(isKit && {
                bundle_label: variant.productName,
                bundle_discount: +(
                  variant.list_price - variant.selling_price
                ).toFixed(2),
                bundle_discount_percentage: +(
                  1 -
                  variant.selling_price / variant.list_price
                ).toFixed(2),
              }),
            },
          ],
        },
      })

      window.localStorage.setItem(
        'algolia-search-info',
        JSON.stringify({
          queryID: variant.__queryID,
          indexName: mainIndex,
          skuId: variant.skuId,
          itemListName,
        })
      )

      // handleEvent({
      //   name: 'search_select_item',
      //   params: {
      //     url: window.location.href,
      //     term: q ?? '',
      //     items: [
      //       {
      //         item_id: variant.productId,
      //         index: variant.index,
      //       },
      //     ],
      //   },
      // })
    },
    [code, variant, userToken, authenticatedUserToken]
  )

  return {
    handleOnClick,
  }
}

export const useAutocompleteAnalytics = () => {
  const userToken = useUserToken()
  const authenticatedUserToken = useAuthenticatedUserToken()

  const sendAutoCompleteAnalyticsEvent = useCallback(
    (eventData: AutocompleteAnalyticsData) => {
      const { topic, objectIDs, queryID, type, state } = eventData
      const eventName =
        type === 'view'
          ? 'search_suggestion_shown'
          : 'search_suggestion_clicked'
      const itemListName = `lt=autocomplete||topic=${topic}||state=${state}`
      // const itemListName = { lt: 'autocomplete', topic, state }

      sendAnalyticsEvent<
        AutocompleteSuggestionsShownEvent | AutocompleteSuggestionClickedEvent
      >({
        name: eventName,
        params: {
          event: 'ga4_event',
          event_name: eventName,
          algolia_user_token: userToken,
          ...(authenticatedUserToken && {
            algolia_authenticated_user_token: authenticatedUserToken,
          }),
          queryID,
          item_list_name: itemListName,
          state,
          suggestion_topic: topic,
          items: objectIDs,
        },
      })
    },
    [userToken, authenticatedUserToken]
  )

  return {
    sendAutoCompleteAnalyticsEvent,
  }
}

export const customFormatSearchPath = (
  term: string,
  redirectUrl: string | undefined = undefined
) => {
  if (redirectUrl) return redirectUrl
  if (!term || term.length === 0) return ''
  return `/s/?q=${encodeURIComponent(term)}`
}

export const formatFacetLabel = (value: string) =>
  value
    .split(/\s*[/>]\s*/)
    .map((part) => part.trim())
    .filter(Boolean)
    .join(' > ')

// Searchbar: facet -> path PLP category/brand
//
// Facets arrive as labels.
// If they don't seem to be already slugified, slugifyCollectionSegment converts them to paths.
//
// Future if BE indexes keys/slugs in facets:
// remove slugifyCollectionSegment and normalizeCollectionSegment;
// use the facet value as is in buildBrandPagePath / buildCategoryPagePath.
//
const slugifyCollectionSegment = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/ /g, '-')
    .replace(/&/g, '-')
    .replace(/[''/\\#,+()$~%.'":*?<>{}!]/g, '-')

const isCollectionSlugKey = (value: string) =>
  /^[a-z0-9]+(?:-[a-z0-9]+)*$/i.test(value.trim())

// Can be removed when facets are only VTEX keys (see comment above).
const normalizeCollectionSegment = (value: string) => {
  const trimmed = value.trim()
  const facetsMapEntries = Object.entries(facetsMap)
  const isValueInFacetsMap = facetsMapEntries.find(
    ([, value]) => value === trimmed
  )
  if (isValueInFacetsMap) {
    return isValueInFacetsMap[0]
  }

  return isCollectionSlugKey(trimmed)
    ? trimmed.toLowerCase()
    : slugifyCollectionSegment(trimmed)
}

export const buildBrandPagePath = (brandFacetValue: string) => {
  const slug = normalizeCollectionSegment(brandFacetValue)
  return slug ? `/brand/${slug}` : customFormatSearchPath(brandFacetValue)
  // return `/brand/${brandFacetValue}`
}

const parseCategoryPathSegments = (categoryPath: string) =>
  categoryPath
    .split(/\s*[/>]\s*/)
    .map((segment) => segment.trim())
    .filter(Boolean)

export const buildCategoryPagePath = (categoryPath: string) => {
  const segments = parseCategoryPathSegments(categoryPath).map((segment) =>
    normalizeCollectionSegment(segment)
  )

  return segments.length > 0
    ? `/${segments.join('/')}`
    : customFormatSearchPath(categoryPath)
}

export const getConvertedFilters = (itemListName: string | undefined) => {
  if (!itemListName) return null

  const convertedFilters = itemListName.split('||').reduce((acc, curr) => {
    const [key, value] = curr.split('=')
    if (key === 'lt' || scalarKeys.has(key)) return acc

    const splittedValue = value.split('|')
    splittedValue.forEach((val) => {
      acc.push(`${key}:${val}`)
    })

    return acc
  }, [] as string[])

  return convertedFilters
}
